import { AdEnginePlugin, type Hooks } from "../../../../core/AdEnginePlugin";
import { loadScript, pushTrack } from "utils";
import type { InstantConfigRepository } from "instant-config";
import { type FandomCmp, LoadTimeEvent } from "common-types";
import type { CaptifyWindowKey } from "./captify";
import { isConsentGranted } from "../../../../helpers/isConsentGranted";

export type CaptifyConfig = Pick<InstantConfigRepository, "icCaptify">;

/**
 * CaptifyPlugin class integrates Captify with the AdEngine.
 * The plugin will not load, until the consent hooks are ran.
 * The plugin loads a script from captify on the page.
 *
 * Original implementation documentation can be found in the original ticket:
 * @see {@link https://fandom.atlassian.net/browse/ADEN-12471}
 */
export class CaptifyPlugin extends AdEnginePlugin {
  private readonly PROPERTY_ID = 12974;
  private readonly CAPTIFY_PIXEL_URL = `https://p.cpx.to/p/${this.PROPERTY_ID}/px.js`;
  // Tracks whether the script was loaded into the page. This prevents multiple loads whenever consents change.
  private isLoaded = false;

  /**
   * Checks if the Captify plugin is enabled.
   * @returns {boolean} True if enabled, false otherwise.
   */
  static isEnabled(config: CaptifyConfig): boolean {
    return Boolean(config?.icCaptify);
  }

  /**
   * Creates the Captify window object.
   */
  private createCaptifyWindowObject(): void {
    const captifyKey: CaptifyWindowKey = `captify_kw_query_${this.PROPERTY_ID}`;
    window[captifyKey] = "";
  }

  /**
   * Creates and inserts the Captify script into the document.
   * This method ensures the script is loaded only once and handles any errors during the script loading process.
   *
   * @returns {Promise<void>} A promise that resolves when the script is successfully loaded.
   */
  private async createAndInsertScript(): Promise<void> {
    try {
      pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
        eventName: "captify_started",
        time: performance.now(),
      });
      await loadScript({
        src: this.CAPTIFY_PIXEL_URL,
        isAsyncOrDeferOtherwise: false,
        node: "first",
      });
      pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
        eventName: "captify_loaded",
        time: performance.now(),
      });
      this.isLoaded = true;
    } catch (error) {
      pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
        eventName: "captify_error_loading",
        time: performance.now(),
      });
      console.error("Failed to load Captify script", error);
    }

    // TODO: Loading status tracking for DW.
  }

  /**
   * Initializes the Captify plugin.
   * The plugin will not initialize if the
   * icCaptify variable in ICBM is not set to true.
   */
  private init(): void {
    if (this.isLoaded) {
      return;
    }

    this.createCaptifyWindowObject();
    this.createAndInsertScript();
  }

  hooks: Hooks = {
    /**
     * Hook that runs when consent is updated.
     * @param {FandomCmp} fandomCmp - The FandomCmp instance containing consent information.
     */
    onConsentUpdated: (fandomCmp: FandomCmp) => {
      if (!isConsentGranted(fandomCmp)) {
        return;
      }

      this.init();
    },
  };
}
